/**
 * DayView — a single day in either density.
 *   compact: a back-to-back vertical list of event cards (no empty time).
 *   full:    an hour timeline with dotted gridlines, free time between events,
 *            and a "now" indicator when the day is today.
 *
 * All-day events render in a strip whose event area is aligned with (and as
 * wide as) the timed event column — the gutter holds the "all-day" label.
 */

import { EventCard } from './EventCard';
import {
  TimelineGutter,
  TimelineGutterHeader,
  TimelineDayColumn,
  NowIndicator,
  gutterWidth,
} from './Timeline';
import { eventsForDay, buildGutterColumns, isSameDay, type TimeBounds } from './calendar.helpers';
import type { NormalizedEvent, CalendarDensity, CalendarDisplay } from './calendar.types';

function EmptyDay() {
  return (
    <div className="flex h-32 items-center justify-center text-sm text-muted-foreground">
      No events this day.
    </div>
  );
}

export interface DayViewProps {
  events: NormalizedEvent[];
  day: Date;
  density: CalendarDensity;
  bounds: TimeBounds;
  now: Date;
  display: CalendarDisplay;
}

export function DayView({ events, day, density, bounds, now, display }: DayViewProps) {
  const dayEvents = eventsForDay(events, day);
  const allDay = dayEvents.filter((e) => e.allDay);
  const timed = dayEvents.filter((e) => !e.allDay);

  if (dayEvents.length === 0) return <EmptyDay />;

  if (density === 'compact') {
    // All-day and timed events share one full-width list.
    return (
      <div className="flex flex-col gap-2 p-4">
        {allDay.map((e) => (
          <EventCard key={e.id} event={e} hour12={display.hour12} />
        ))}
        {timed.map((e) => (
          <EventCard key={e.id} event={e} hour12={display.hour12} />
        ))}
      </div>
    );
  }

  const columns = buildGutterColumns(day, display.timezones);
  const gutterW = gutterWidth(columns);
  const hasZones = columns.length > 1;

  return (
    <div className="flex flex-col px-4 py-4">
      {/* All-day strip — event area aligned with the timed column */}
      {allDay.length > 0 && (
        <div className="mb-3 flex">
          <div
            className="flex shrink-0 items-center justify-end pr-2 text-[11px] text-muted-foreground"
            style={{ width: gutterW }}
          >
            all-day
          </div>
          <div className="flex flex-1 flex-col gap-2">
            {allDay.map((e) => (
              <EventCard key={e.id} event={e} compact hour12={display.hour12} />
            ))}
          </div>
        </div>
      )}

      {hasZones && (
        <div className="mb-3 flex">
          <TimelineGutterHeader columns={columns} />
          <div className="flex-1" />
        </div>
      )}

      <div className="relative flex">
        <TimelineGutter columns={columns} day={day} bounds={bounds} hour12={display.hour12} />
        <TimelineDayColumn events={timed} bounds={bounds} hour12={display.hour12} />
        {isSameDay(day, now) && (
          <NowIndicator bounds={bounds} now={now} gutterW={gutterW} hour12={display.hour12} />
        )}
      </div>
    </div>
  );
}
